import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import ExplainPanel from "components/ExplainPanel";
import type { ExplainPlanType, QuerySampleType } from "utils/explain";
import { useWorkbookResultQuery } from "./gql/Query.generated";

import Loading from "components/Loading";
import PageContent from "components/PageContent";
import { WorkbookFeatureNav } from "../Workbook";
import { useRoutes } from "utils/routes";

const WorkbookResult = () => {
  const { databaseId, workbookId, variantId, resultId } = useParams();
  const navigate = useNavigate();
  const { databaseWorkbookVariantResult } = useRoutes();

  const { loading, error, data } = useWorkbookResultQuery({
    variables: {
      databaseId,
      workbookId,
      variantId: variantId,
      resultId,
    },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const blockSize = data.getServerDetails.blockSize;
  const workbookResult = data.getWorkbookResult;
  const workbook = data.getWorkbookDetails;
  const variants = [
    data.getWorkbookDetails.baselineQuery,
    ...data.getWorkbookDetails.variants,
  ];
  const variant = variants.find((val) => val.id === variantId);
  const paramSet = workbookResult.usedParameterSet;
  const handleVariantChange = (variantId: string) => {
    if (variantId !== variant.id) {
      const newVariant = variants.find((variant) => variant.id === variantId);
      // If a new variant doesn't have current param set's result yet, show the first result instead
      const newResultId =
        newVariant.workbookResults.find(
          (result) => paramSet && result.parameterSetId === paramSet.id,
        )?.id || newVariant.workbookResults[0].id;
      return navigate(
        databaseWorkbookVariantResult(
          databaseId,
          workbookId,
          newVariant.id,
          newResultId,
        ),
      );
    }
  };
  const handleParameterSetChange = (setId: string) => {
    if (setId !== paramSet.id) {
      const newResultId = variant.workbookResults.find(
        (result) => result.parameterSetId === setId,
      ).id;
      return navigate(
        databaseWorkbookVariantResult(
          databaseId,
          workbookId,
          variantId,
          newResultId,
        ),
      );
    }
  };

  const explainPlan: ExplainPlanType = {
    id: workbookResult.id,
    humanId: workbookResult.id,
    seenAt: workbookResult.createdAt,
    querySample: {
      id: workbookResult.id,
      occurredAt: workbookResult.createdAt,
      runtimeMs: workbookResult.runtimeMs,
      queryText: workbookResult.queryText,
    } as QuerySampleType,
    query: { id: workbookResult.id },
    format: workbookResult.format,
    fingerprint: workbookResult.planFingerprint,
    outputText: workbookResult.outputText,
    annotatedJson: workbookResult.annotatedJson,
    totalCost: workbookResult.totalCost,
    totalSharedBlksRead: workbookResult.totalSharedBlksRead,
    totalBlkReadTime: workbookResult.totalBlkReadTime,
    advisorInsights: workbookResult.nodeInsights,
  };
  const title = (
    <div className="text-[#606060]">
      <h2 className="m-0 py-[9px] font-medium leading-[26px] text-[22px]">
        {workbook.name}
      </h2>
      <div className="flex">
        <div className="mr-4">
          Variant:{" "}
          <select
            value={variant.id}
            onChange={(e) => handleVariantChange(e.target.value)}
            className="bg-inherit"
          >
            {variants.map((variant) => {
              if (variant.workbookResults.length === 0) {
                return null;
              }
              return (
                <option key={variant.id} value={variant.id}>
                  {variant.name}
                </option>
              );
            })}
          </select>
        </div>
        {paramSet && (
          <div>
            Parameter Set:{" "}
            <select
              value={paramSet.id}
              onChange={(e) => handleParameterSetChange(e.target.value)}
              className="bg-inherit"
            >
              {workbook.parameterSets.map((set) => {
                return (
                  <option key={set.id} value={set.id}>
                    {set.name}
                  </option>
                );
              })}
            </select>
          </div>
        )}
      </div>
    </div>
  );
  return (
    <PageContent
      windowTitle={`EXPLAIN Workbook: ${workbook.name}`}
      featureInfo={title}
      pageCategory="workbooks"
      pageName="show-result"
      layout="bare"
      featureNav={
        <WorkbookFeatureNav workbookId={workbookId} databaseId={databaseId} />
      }
    >
      <ExplainPanel
        explain={explainPlan}
        databaseId={databaseId}
        blockSize={blockSize}
        relatedWorkbookId={workbookId}
        hideComparison
      />
    </PageContent>
  );
};

export default WorkbookResult;
